<template>
  <div class="space-y-6">
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-xl font-semibold text-gray-100">Product Activation Rules</h2>
        <p class="mt-1 text-sm text-gray-400">
          Rules control product visibility. Rule groups run in order - drag to reorder priority.<br>
        </p>
      </div>
      <button
        @click="showNewGroupModal = true"
        class="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
        </svg>
        Add Rule Group
      </button>
    </div>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="flex flex-col items-center justify-center rounded-lg border border-gray-700 bg-dark-secondary py-12"
    >
      <svg class="h-12 w-12 text-gray-400 animate-spin" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-200">Loading rules...</h3>
    </div>

    <!-- Rule Groups -->
    <TransitionGroup v-else tag="div" class="space-y-4" name="rule-group">
      <div
        v-for="(group, index) in ruleGroups"
        :key="group.id"
        draggable="true"
        @dragstart="dragStart($event, group)"
        @dragend="dragEnd"
        @dragover.prevent="dragOver($event, group)"
        @dragenter.prevent
        @drop.prevent="updateGroupOrders"
        class="rounded-lg border border-gray-700 bg-dark-secondary transition-all duration-200"
        :class="{ 
          'opacity-50': draggingGroup?.id === group.id, 
          'border-primary shadow-lg': isDragTarget(group),
          'transform scale-[0.98] bg-dark-secondary/80': draggingGroup?.id === group.id
        }"
      >
        <!-- Group Header -->
        <div class="flex items-center border-b border-gray-700 p-4">
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <!-- Order Number -->
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-gray-400" :title="'Execution order: ' + (index + 1)">
                #{{ index + 1 }}
              </span>
              <!-- Drag Handle -->
              <div class="cursor-move p-1 hover:bg-gray-700/50 rounded" title="Drag to reorder execution priority">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
                </svg>
              </div>
            </div>
            <button
              @click="toggleRuleGroup(group.id)"
              class="rounded-md p-1 hover:bg-gray-700/50"
              :title="group.isActive ? 'Disable group' : 'Enable group'"
            >
              <div class="h-5 w-5">
                <svg
                  v-if="group.isActive"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  class="text-emerald-500"
                >
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
                </svg>
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  class="text-gray-500"
                >
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" />
                </svg>
              </div>
            </button>
            <div class="flex items-center gap-2 -ml-2">
              <button
                @click="toggleGroupCollapse(group.id)"
                class="rounded hover:bg-gray-700/30 p-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-gray-400 transition-transform"
                  :class="{ 'rotate-90': !collapsedGroups.has(group.id) }"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
              <input
                v-model="group.name"
                @change="updateRuleGroup(group.id, { name: group.name })"
                @blur="updateRuleGroup(group.id, { name: group.name })"
                class="bg-transparent text-lg font-medium text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1 hover:bg-gray-700/30 w-80"
                :placeholder="'Enter group name'"
              />
            </div>
            
          </div>
          <div class="flex items-center gap-2 ml-auto">
            <div class="flex items-center gap-2">
              <span
                :class="[
                  'rounded-full px-2 py-1 text-xs font-medium',
                  group.isActive
                    ? 'bg-emerald-500/10 text-emerald-500'
                    : 'bg-gray-500/10 text-gray-500'
                ]"
              >
                {{ group.isActive ? 'Active' : 'Inactive' }}
              </span>
              <span
                :class="[
                  'rounded-full px-2 py-1 text-xs font-medium',
                  group.outcome === 'active'
                    ? 'bg-blue-500/10 text-blue-500'
                    : 'bg-red-500/10 text-red-500'
                ]"
              >
                Set products {{ group.outcome }}
              </span>
            </div>
            <!-- Save Button -->
            <button
              v-if="hasUnsavedChanges(group.id)"
              @click="saveRuleGroup(group.id)"
              :disabled="isSaving(group.id)"
              :class="[
                'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                isSaveSuccess(group.id)
                  ? 'border-emerald-500 bg-emerald-500/10 text-emerald-500'
                  : 'border-primary bg-primary/10 text-primary hover:bg-primary/20',
                'disabled:opacity-50'
              ]"
            >
              <svg
                v-if="isSaving(group.id)"
                class="h-4 w-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {{ isSaving(group.id) ? 'Saving...' : 'Save Changes' }}
            </button>

            <!-- Add Rule Button -->
            <button
              @click="addRule(group.id)"
              class="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-3 py-1.5 text-sm font-medium text-gray-200 hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
            >
              Add Rule
            </button>

            <!-- Delete Group Button -->
            <button
              @click="deleteRuleGroup(group.id)"
              class="rounded-lg p-2 text-gray-400 hover:bg-red-500/10 hover:text-red-500"
              title="Delete group"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        <div v-show="!collapsedGroups.has(group.id)">
          <!-- Group Settings -->
          <div class="flex items-center gap-4 border-b border-gray-700 p-4 bg-gray-800/30">
            <!-- Logical Operator -->
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-400">Combine rules with:</span>
              <div class="flex rounded-lg border border-gray-700 p-1">
                <button
                  v-for="(label, operator) in LOGICAL_OPERATOR_LABELS"
                  :key="operator"
                  @click="updateRuleGroup(group.id, { logicalOperator: operator })"
                  :class="[
                    'px-3 py-1 text-sm font-medium rounded transition-colors',
                    group.logicalOperator === operator
                      ? 'bg-primary text-white'
                      : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'
                  ]"
                >
                  {{ operator }}
                </button>
              </div>
            </div>

            <!-- Outcome -->
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-400">When rules match:</span>
              <div class="flex rounded-lg border border-gray-700 p-1">
                <button
                  v-for="outcome in ['active', 'inactive'] as const"
                  :key="outcome"
                  @click="updateRuleGroup(group.id, { outcome })"
                  :class="[
                    'px-3 py-1 text-sm font-medium rounded transition-colors',
                    group.outcome === outcome
                      ? outcome === 'active' ? 'bg-blue-500 text-white' : 'bg-red-500 text-white'
                      : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'
                  ]"
                >
                  Set {{ outcome }}
                </button>
              </div>
            </div>
          </div>

          <!-- Rules List -->
          <div class="divide-y divide-gray-700">
            <div
            v-for="(rule, ruleIndex) in group.rules"
            :key="ruleIndex"
            class="flex items-center gap-4 p-4"
          >
            <!-- Field -->
            <div class="w-48">
              <select
                v-model="rule.field"
                @change="updateOperatorForField(group.id, ruleIndex, rule.field)"
                class="block w-full rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option
                  v-for="(label, field) in FIELD_LABELS"
                  :key="field"
                  :value="field"
                >
                  {{ label }}
                </option>
              </select>
            </div>

            <!-- Operator -->
            <div class="w-48">
              <select
                v-model="rule.operator"
                class="block w-full rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option
                  v-for="operator in FIELD_OPERATORS[rule.field]"
                  :key="operator"
                  :value="operator"
                >
                  {{ OPERATOR_LABELS[operator] }}
                </option>
              </select>
            </div>

            <!-- Value -->
            <div class="flex-1">
              <!-- Product Type Dropdown -->
              <select
                v-if="rule.field === 'product_type'"
                v-model="rule.value"
                class="block w-full rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">Select Product Type</option>
                <option
                  v-for="type in rulesStore.availableProductTypes"
                  :key="type"
                  :value="type"
                >
                  {{ type }}
                </option>
              </select>

              <!-- Tags Dropdown -->
              <select
                v-else-if="rule.field === 'tags'"
                v-model="rule.value"
                class="block w-full rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">Select Tag</option>
                <option
                  v-for="tag in rulesStore.availableTags"
                  :key="tag"
                  :value="tag"
                >
                  {{ tag }}
                </option>
              </select>

              <!-- Default Input -->
              <input
                v-else
                v-model="rule.value"
                :type="rule.field === 'inventory_qty' || rule.field === 'price' ? 'number' : 'text'"
                class="block w-full rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                :placeholder="getValuePlaceholder(rule.field)"
              >
            </div>

            <!-- Delete Rule -->
            <button
              @click="deleteRule(group.id, ruleIndex)"
              class="rounded-lg p-2 text-gray-400 hover:bg-red-500/10 hover:text-red-500"
              title="Delete rule"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>

          <!-- Empty State -->
          <div
            v-if="group.rules.length === 0"
            class="flex flex-col items-center justify-center py-8 text-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-200">No rules yet</h3>
            <p class="mt-1 text-sm text-gray-400">
              Get started by adding a rule to this group
            </p>
            <div class="mt-4">
              <button
                @click="addRule(group.id)"
                class="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
                </svg>
                Add First Rule
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-if="!isLoading && ruleGroups.length === 0"
        class="flex flex-col items-center justify-center rounded-lg border border-gray-700 bg-dark-secondary py-12"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-200">No rule groups</h3>
        <p class="mt-1 text-sm text-gray-400">
          Get started by creating a new rule group
        </p>
        <div class="mt-6">
          <button
            @click="showNewGroupModal = true"
            class="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
            </svg>
            Create Rule Group
          </button>
        </div>
      </div>
    </TransitionGroup>

    <!-- New Group Modal -->
    <BaseModal
      v-model="showNewGroupModal"
    >
      <template #title>Create New Rule Group</template>
      
      <div>
        <label for="groupName" class="block text-sm font-medium text-gray-200">
          Group Name
        </label>
        <input
          id="groupName"
          v-model="newGroupName"
          type="text"
          class="mt-1 block w-full rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder="Enter group name"
        >
      </div>

      <template #footer>
        <button
          @click="createRuleGroup"
          :disabled="!newGroupName.trim()"
          class="inline-flex w-full justify-center rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 sm:ml-3 sm:w-auto"
        >
          Create Group
        </button>
        <button
          @click="showNewGroupModal = false"
          class="mt-3 inline-flex w-full justify-center rounded-lg border border-gray-700 px-3 py-2 text-sm font-medium text-gray-300 shadow-sm hover:bg-gray-700/50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:w-auto"
        >
          Cancel
        </button>
      </template>
    </BaseModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRulesStore } from '@/stores/rules'
import { FIELD_LABELS, OPERATOR_LABELS, FIELD_OPERATORS, LOGICAL_OPERATOR_LABELS } from '@/types/rules'
import type { RuleField, LogicalOperator, RuleGroup } from '@/types/rules'
import BaseModal from '@/components/ui/BaseModal.vue'

const rulesStore = useRulesStore()
const showNewGroupModal = ref(false)
const newGroupName = ref('')

// Get loading state from store
const isLoading = computed(() => rulesStore.loading)

// Initialize with all groups collapsed
const collapsedGroups = ref<Set<string>>(new Set())
const draggingGroup = ref<RuleGroup | null>(null)
const dragTarget = ref<RuleGroup | null>(null)

const ruleGroups = computed(() => rulesStore.sortedRuleGroups)

// Watch for changes in ruleGroups and ensure new groups are collapsed by default
watch(ruleGroups, (newGroups) => {
  newGroups.forEach(group => {
    if (!collapsedGroups.value.has(group.id)) {
      collapsedGroups.value.add(group.id)
    }
  })
}, { immediate: true })

// Drag and drop handlers
const dragStart = (event: DragEvent, group: RuleGroup) => {
  if (!event.dataTransfer) return
  draggingGroup.value = group
  event.dataTransfer.effectAllowed = 'move'
}

const dragEnd = () => {
  draggingGroup.value = null
  dragTarget.value = null
}

const dragOver = (event: DragEvent, targetGroup: RuleGroup) => {
  if (!draggingGroup.value || draggingGroup.value.id === targetGroup.id) return
  dragTarget.value = targetGroup
}

const isDragTarget = (group: RuleGroup): boolean => {
  return dragTarget.value?.id === group.id
}

// Reorder groups and update orders
const updateGroupOrders = async () => {
  if (!draggingGroup.value || !dragTarget.value) return

  const groups = [...ruleGroups.value]
  const sourceIndex = groups.findIndex(g => g.id === draggingGroup.value?.id)
  const targetIndex = groups.findIndex(g => g.id === dragTarget.value?.id)
  
  if (sourceIndex === -1 || targetIndex === -1) return

  // Remove the dragged group
  const [movedGroup] = groups.splice(sourceIndex, 1)
  // Insert it at the new position
  groups.splice(targetIndex, 0, movedGroup)

  // Update orders
  groups.forEach((group, index) => {
    group.order = index
  })

  // Save all groups with new orders
  try {
    await Promise.all(groups.map(group => rulesStore.saveRuleGroup(group.id)))
  } catch (error) {
    console.error('Failed to update group orders:', error)
  }
}

const toggleGroupCollapse = (groupId: string) => {
  if (collapsedGroups.value.has(groupId)) {
    collapsedGroups.value.delete(groupId)
  } else {
    collapsedGroups.value.add(groupId)
  }
}

const createRuleGroup = async () => {
  if (newGroupName.value.trim()) {
    const result = await rulesStore.addRuleGroup(newGroupName.value.trim(), 'active')
    // New groups start expanded (not added to collapsedGroups)
    newGroupName.value = ''
    showNewGroupModal.value = false
  }
}

const addRule = (groupId: string) => {
  collapsedGroups.value.delete(groupId);
  rulesStore.addRule(groupId, {
    field: 'inventory_qty',
    operator: FIELD_OPERATORS.inventory_qty[0],
    value: ''
  })
}

const updateOperatorForField = (groupId: string, ruleIndex: number, field: RuleField) => {
  // When field changes, set the operator to the first valid operator for that field
  rulesStore.updateRule(groupId, ruleIndex, {
    operator: FIELD_OPERATORS[field][0]
  })
}

const updateRuleGroup = (groupId: string, updates: Partial<RuleGroup>) => {
  rulesStore.updateRuleGroup(groupId, updates)
}

const deleteRule = (groupId: string, ruleIndex: number) => {
  rulesStore.deleteRule(groupId, ruleIndex)
}

const deleteRuleGroup = (groupId: string) => {
  rulesStore.deleteRuleGroup(groupId)
}

const toggleRuleGroup = (groupId: string) => {
  rulesStore.toggleRuleGroup(groupId)
}

const hasUnsavedChanges = (groupId: string): boolean => {
  return rulesStore.hasUnsavedChanges(groupId)
}

const isSaving = (groupId: string): boolean => {
  return rulesStore.isSaving(groupId)
}

const isSaveSuccess = (groupId: string): boolean => {
  return rulesStore.isSaveSuccess(groupId)
}

const saveRuleGroup = async (groupId: string) => {
  try {
    await rulesStore.saveRuleGroup(groupId)
  } catch (error) {
    console.error('Failed to save rule group:', error)
  }
}

const getValuePlaceholder = (field: RuleField): string => {
  switch (field) {
    case 'inventory_qty':
      return 'Enter quantity'
    case 'price':
      return 'Enter price'
    case 'product_type':
      return 'Enter product type'
    case 'tags':
      return 'Enter tag'
    case 'title':
      return 'Enter title'
    default:
      return 'Enter value'
  }
}

// Load rules when component mounts
onMounted(async () => {
  try {
    await rulesStore.loadRules()
  } catch (error) {
    console.error('Failed to load rules:', error)
  }
})
</script>

<style scoped>
.rule-group-move {
  transition: transform 0.5s ease;
}
</style>